/**
 * App.jsx - Scriptus Pharma Partner
 * Aplicação principal do frontend de farmácias
 * 
 * ✅ PrivateRoute extraído para arquivo separado
 * ✅ Verificação de sessão ativa
 * ✅ Controle de plano Basic/Partner
 * ✅ Verificação de limites de dispensação
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';

// Componentes de Rota
import PrivateRoute, { PublicRoute } from './components/PrivateRoute';

// Páginas
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import DispensationPage from './pages/DispensationPage';
import HistoryPage from './pages/HistoryPage';
import Configuracoes from './pages/Configuracoes';
import Assinatura from './pages/Assinatura';
import CatalogoProdutos from './pages/CatalogoProdutos';

// Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutos
      retry: 1
    }
  }
});

// ============================================
// LAYOUT PRINCIPAL
// ============================================

function AppLayout({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      {children}
    </div>
  );
}

// ============================================
// APP ROUTES
// ============================================

function AppRoutes() {
  return (
    <Routes>
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ROTAS PÚBLICAS */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      
      {/* Rota Inicial - Register como Landing Page */}
      <Route path="/" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      
      {/* Rota /register redireciona para / */}
      <Route path="/register" element={<Navigate to="/" replace />} />
      
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ROTAS PRIVADAS - BASIC + PARTNER */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      
      {/* Dashboard - Todos podem acessar */}
      <Route path="/dashboard" element={
        <PrivateRoute>
          <Dashboard />
        </PrivateRoute>
      } />
      
      {/* Dispensação - Verifica limite para Basic */}
      <Route path="/dispensar" element={
        <PrivateRoute checkDispensationLimit={true}>
          <DispensationPage />
        </PrivateRoute>
      } />
      
      {/* Dispensação com token - Verifica limite para Basic */}
      <Route path="/dispensar/:token" element={
        <PrivateRoute checkDispensationLimit={true}>
          <DispensationPage />
        </PrivateRoute>
      } />
      
      {/* Histórico - Todos podem acessar */}
      <Route path="/historico" element={
        <PrivateRoute>
          <HistoryPage />
        </PrivateRoute>
      } />
      
      {/* Configurações - Todos podem acessar */}
      <Route path="/configuracoes" element={
        <PrivateRoute>
          <Configuracoes />
        </PrivateRoute>
      } />
      
      {/* Assinatura - Todos podem acessar (para upgrade) */}
      <Route path="/assinatura" element={
        <PrivateRoute>
          <Assinatura />
        </PrivateRoute>
      } />
      
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ROTAS PRIVADAS - SOMENTE PARTNER */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      
      {/* Catálogo de Produtos - Somente Partner */}
      <Route path="/catalogo-produtos" element={
        <PrivateRoute requirePartner={true}>
          <CatalogoProdutos />
        </PrivateRoute>
      } />
      
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* 404 */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Route path="*" element={
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
          <div className="text-center">
            <div className="glass-card-elevated rounded-2xl p-8 sm:p-12 shadow-lg border border-gray-200/50 max-w-md mx-auto">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h1 className="text-6xl sf-pro-display font-bold text-gray-900 mb-4">404</h1>
              <p className="text-xl sf-pro-text text-gray-600 mb-8">Página não encontrada</p>
              <a 
                href="/dashboard" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-semibold sf-pro-text transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Voltar ao Dashboard
              </a>
            </div>
          </div>
        </div>
      } />
    </Routes>
  );
}

// ============================================
// APP PRINCIPAL
// ============================================

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppLayout>
            <AppRoutes />
          </AppLayout>
          
          {/* Toast Notifications */}
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: '#1f2937',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                padding: '12px 16px',
                fontFamily: '-apple-system, "SF Pro Text", BlinkMacSystemFont, sans-serif'
              },
              success: {
                iconTheme: { primary: '#10b981', secondary: '#fff' },
                style: { border: '1px solid rgba(16, 185, 129, 0.3)' }
              },
              error: {
                iconTheme: { primary: '#ef4444', secondary: '#fff' },
                style: { border: '1px solid rgba(239, 68, 68, 0.3)' }
              },
              loading: {
                iconTheme: { primary: '#3b82f6', secondary: '#fff' }
              }
            }}
          />
        </AuthProvider>
      </BrowserRouter>
      
      {/* Global Styles */}
      <style>{`
        .sf-pro-display {
          font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
          font-feature-settings: "liga" 1, "kern" 1;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        .sf-pro-text {
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
          font-feature-settings: "liga" 1, "kern" 1;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        .glass-card-elevated {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.7),
            inset 0 -1px 0 rgba(255, 255, 255, 0.3);
        }
      `}</style>
    </QueryClientProvider>
  );
}